import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Plus, ListChecks } from 'lucide-react';

import { useAuth } from '@/auth/AuthProvider';
import {
  getSummary,
  getTrends,
  getCategoryDistribution,
  getBmiDistribution,
} from '@/api/analytics';
import { Spinner } from '@/components/feedback/Spinner';
import { TrendChart } from '@/components/charts/TrendChart';
import { BmiGauge } from '@/components/charts/BmiGauge';
import { CategoryDonut } from '@/components/charts/CategoryDonut';
import { KpiCard } from '@/components/charts/KpiCard';

const RANGE_OPTIONS: { label: string; days: number }[] = [
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '365d', days: 365 },
];

export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [range, setRange] = useState<number>(30);
  const [metric, setMetric] = useState<'weight' | 'bmi'>('weight');

  const summaryQuery = useQuery({
    queryKey: ['analytics', 'summary', range],
    queryFn: () => getSummary(range),
  });
  const trendsQuery = useQuery({
    queryKey: ['analytics', 'trends', range, metric],
    queryFn: () => getTrends(range, metric),
  });
  const catDistQuery = useQuery({
    queryKey: ['analytics', 'dist:cat', range],
    queryFn: () => getCategoryDistribution(range),
  });
  const bmiDistQuery = useQuery({
    queryKey: ['analytics', 'dist:bmi', range],
    queryFn: () => getBmiDistribution(range),
  });

  const summary = summaryQuery.data;
  const isLoading =
    summaryQuery.isLoading ||
    trendsQuery.isLoading ||
    catDistQuery.isLoading ||
    bmiDistQuery.isLoading;

  const units = user?.units ?? 'metric';
  const weightUnit = units === 'imperial' ? 'lb' : 'kg';
  // Display weight in lb when user prefers imperial (1 kg = 2.20462 lb)
  const formatWeight = (kg: string | null): string => {
    if (kg == null) return '—';
    const n = Number(kg);
    if (!Number.isFinite(n)) return '—';
    return units === 'imperial' ? (n * 2.20462).toFixed(1) : n.toFixed(1);
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-slate-100">
            {t('dashboard.welcome', { name: user?.displayName ?? '' })}
          </h1>
          <p className="mt-1 text-slate-600 dark:text-slate-400">{t('app.tagline')}</p>
        </div>

        <div role="tablist" aria-label="Range" className="inline-flex rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-1">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.days}
              role="tab"
              aria-selected={range === opt.days}
              onClick={() => setRange(opt.days)}
              className={[
                'px-3 py-1 text-sm rounded-md transition',
                range === opt.days
                  ? 'bg-brand-600 text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800',
              ].join(' ')}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </header>

      {/* KPIs */}
      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label={t('records.title')}
          value={summary?.totalRecords ?? 0}
          hint={summary ? `${summary.recordsInRange} in ${range}d` : '—'}
        />
        <KpiCard
          label={t('dashboard.kpi.latestWeight', { unit: weightUnit })}
          value={formatWeight(summary?.latestWeightKg ?? null)}
          hint={summary?.latestDate ? new Date(summary.latestDate).toLocaleDateString() : '—'}
          trend={summary?.weightChangePct ? Number(summary.weightChangePct) : null}
        />
        <KpiCard
          label={t('dashboard.kpi.avgWeight', { unit: weightUnit })}
          value={formatWeight(summary?.avgWeightKg ?? null)}
          hint={t('dashboard.kpi.avgHint', { range })}
        />
        <KpiCard
          label={t('dashboard.kpi.weightChange', { unit: weightUnit })}
          value={summary?.weightChangeKg ? formatWeight(summary.weightChangeKg) : '—'}
          hint={summary?.startWeightKg ? `start: ${formatWeight(summary.startWeightKg)} ${weightUnit}` : '—'}
          trend={summary?.weightChangePct ? Number(summary.weightChangePct) : null}
        />
      </section>

      {/* Trends + Gauge */}
      <section className="grid gap-4 lg:grid-cols-3">
        <div className="card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-900 dark:text-slate-100">
              {t('dashboard.trends.title')}
            </h2>
            <div role="tablist" aria-label="Metric" className="inline-flex rounded-md border border-slate-200 dark:border-slate-700 text-xs">
              <button
                role="tab"
                aria-selected={metric === 'weight'}
                onClick={() => setMetric('weight')}
                className={[
                  'px-3 py-1 rounded-l-md transition',
                  metric === 'weight'
                    ? 'bg-brand-600 text-white'
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800',
                ].join(' ')}
              >
                {t('dashboard.trends.weight')}
              </button>
              <button
                role="tab"
                aria-selected={metric === 'bmi'}
                onClick={() => setMetric('bmi')}
                className={[
                  'px-3 py-1 rounded-r-md transition',
                  metric === 'bmi'
                    ? 'bg-brand-600 text-white'
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800',
                ].join(' ')}
              >
                BMI
              </button>
            </div>
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Spinner />
            </div>
          ) : (
            <TrendChart
              data={trendsQuery.data ?? []}
              metric={metric}
              bmiBand={{ min: 18.5, max: 25 }}
              unitLabel={metric === 'weight' ? weightUnit : ''}
            />
          )}
        </div>

        <div className="card p-5">
          <h2 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">
            {t('dashboard.bmi.title')}
          </h2>
          {summary ? (
            <BmiGauge bmi={summary.latestBmi ? Number(summary.latestBmi) : null} />
          ) : (
            <div className="flex items-center justify-center h-48">
              <Spinner />
            </div>
          )}
          {bmiDistQuery.data && (
            <dl className="mt-4 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <div className="flex justify-between">
                <dt className="text-sky-600 dark:text-sky-400">{t('records.bmiCategory.underweight')}</dt>
                <dd className="tabular-nums">{bmiDistQuery.data.underweight}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-emerald-600 dark:text-emerald-400">{t('records.bmiCategory.normal')}</dt>
                <dd className="tabular-nums">{bmiDistQuery.data.normal}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-amber-600 dark:text-amber-400">{t('records.bmiCategory.overweight')}</dt>
                <dd className="tabular-nums">{bmiDistQuery.data.overweight}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-rose-600 dark:text-rose-400">{t('records.bmiCategory.obese')}</dt>
                <dd className="tabular-nums">{bmiDistQuery.data.obese}</dd>
              </div>
            </dl>
          )}
        </div>
      </section>

      {/* Category distribution + quick actions */}
      <section className="grid gap-4 lg:grid-cols-3">
        <div className="card p-5 lg:col-span-2">
          <h2 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">
            {t('dashboard.distribution.title')}
          </h2>
          <CategoryDonut data={catDistQuery.data ?? []} />
        </div>

        <div className="card p-5 flex flex-col gap-3">
          <h2 className="font-semibold text-slate-900 dark:text-slate-100">
            {t('dashboard.quickActions')}
          </h2>
          <Link to="/records/new" className="btn-primary">
            <Plus className="h-4 w-4" />
            {t('records.addNew')}
          </Link>
          <Link to="/records" className="btn-secondary">
            <ListChecks className="h-4 w-4" />
            {t('nav.records')}
          </Link>
        </div>
      </section>

      {/* i18n-noop: keeps TypeScript happy about t() in this file even when key is conditional */}
      <span className="hidden">{i18n.language}</span>
    </div>
  );
}
